import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DynamicGmap } from '@kyruus/gmap';
import { MapContainer } from './styles';

/**
 * ProviderMapContainer component displays a map view next to the provider list
 * when the provider_list_maps_enabled feature flag is enabled
 * Shows all provider locations as pins on the map
 *
 * Expected data structure for providers:
 * providers = [
 *   {
 *     id: string,
 *     name: string,
 *     locations: [
 *       {
 *         id: string,
 *         name: string,
 *         address: {
 *           coordinates: {
 *             lat: number,
 *             lon: number
 *           }
 *         }
 *       }
 *     ]
 *   }
 * ]
 */
function ProviderMapContainer({ providers, config }) {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const mapBoundsRef = useRef(null);
  const [mapLocations, setMapLocations] = useState([]);
  const [locationIdsSet, setLocationIdsSet] = useState(new Set());

  // Cleanup function to prevent memory leaks when component unmounts
  useEffect(() => {
    return () => {
      // Clean up refs when component unmounts
      if (mapRef.current) {
        mapRef.current = null;
      }
      if (googleMapRef.current) {
        googleMapRef.current = null;
      }
      if (mapBoundsRef.current) {
        mapBoundsRef.current = null;
      }
    };
  }, []);

  // Process provider data when it changes (including pagination)
  useEffect(() => {
    if (!providers || !config) {
      return;
    }

    // Extract, filter, and format all valid locations from providers in one chain
    const formattedLocations = providers
      .flatMap((provider) =>
        (provider.locations || [])
          .filter((location) => location && location.coordinates)
          .map((location) => ({
            id: location.id || `${provider.id}-${location.name}`,
            name: location.name || provider.name,
            address: location
          }))
      )
      .map((location, index) => ({
        id: `${location.id || 'loc'}-${index}`, // Ensure truly unique IDs by appending index
        name: `Provider at ${location.name}`,
        coordinates: location.address.coordinates
      }));

    // Update state with new locations
    setMapLocations(formattedLocations);
    setLocationIdsSet(new Set(formattedLocations.map((loc) => loc.id)));
  }, [providers, config]);

  // If there are no providers or no config, return null
  if (!providers || !config || mapLocations.length === 0) {
    return null;
  }

  return (
    <MapContainer data-testid="provider-map-container">
      <DynamicGmap
        locations={mapLocations}
        enabledLocationIds={locationIdsSet}
        mapOptions={{
          maxZoom: 16,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: true,
          streetViewControl: true,
          styles: [
            // Simplify points of interest
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            // Simplify road labels
            {
              featureType: 'road',
              elementType: 'labels.icon',
              stylers: [{ visibility: 'off' }]
            },
            // Keep transit visible but simplified
            { featureType: 'transit', stylers: [{ visibility: 'simplified' }] }
          ]
        }}
        shouldLabelMarkers={true}
        theme={config.theme}
        getMarkers={(markers) => {
          // Store markers for potential future use
          mapRef.current = markers;
        }}
        mapEventsToRegister={{
          // Fit bounds when map is fully loaded
          idle: (mapInstanceRef) => {
            // Only fit bounds once when the map first loads
            if (
              mapInstanceRef.current &&
              mapLocations.length > 0 &&
              !googleMapRef.current
            ) {
              googleMapRef.current = mapInstanceRef.current;

              // Create bounds object if google is available
              if (window.google && window.google.maps) {
                const bounds = new window.google.maps.LatLngBounds();

                // Add each location to the bounds
                mapLocations.forEach((location) => {
                  bounds.extend({
                    lat: location.coordinates.lat,
                    lng: location.coordinates.lon
                  });
                });

                // Store bounds for future reference
                mapBoundsRef.current = bounds;

                // Fit the map to the bounds with padding
                mapInstanceRef.current.fitBounds(bounds, {
                  // Add padding to ensure markers aren't right at the edge
                  top: 50,
                  right: 50,
                  bottom: 50,
                  left: 50
                });
              }
            }
          }
        }}
      />
    </MapContainer>
  );
}

ProviderMapContainer.propTypes = {
  // Array of provider objects with location data
  providers: PropTypes.array,
  // Configuration object with theme settings
  config: PropTypes.object
};

ProviderMapContainer.defaultProps = {
  providers: [],
  config: null
};

export default ProviderMapContainer;
